import { useState, useEffect } from "react";
import { Redirect } from "react-router-dom";
import { useParams } from "react-router";
import ReactGA from "react-ga4";
import { useGrowthBook } from "@growthbook/growthbook-react";
import * as Sentry from "@sentry/react";

import { Box } from "@mui/material";

import { AnimatedLogo } from "@aclymatepackages/atoms";

import ErrorBoundary from "./atoms/ErrorBoundary";

import CompanyLayout from "./layouts/CompanyLayout";

import OnboardingChecklist from "./platform-pages/onboarding";
import Dashboard from "./platform-pages/dashboard";
import Settings from "./platform-pages/settings";
import Purchase from "./platform-pages/purchase";
import Badges from "./platform-pages/badges";
import Reporting from "./platform-pages/reporting";
import Accounting from "./platform-pages/accounting";
import Events from "./platform-pages/events";
import Products from "./platform-pages/products";

import ExpiredAccountFootprintSummary from "./modules/ExpiredSubscriptionFootprintSummary";

import { useAuth, signOut } from "../helpers/firebase";
import { analyticsIdentify } from "../helpers/analytics";
import { useSubscriptionType } from "../helpers/hooks/companyData";
import { getAccountId } from "../helpers/accountHelpers";
import { StripeCustomerContextProvider } from "../helpers/contexts/stripeCustomer";
import { getUserAccountData } from "../helpers/components/accountAccess";

const LoadingScreen = () => (
  <Box
    style={{ height: "100vh", width: "100%" }}
    display="flex"
    alignItems="center"
    justifyContent="center"
  >
    <AnimatedLogo />
  </Box>
);

const SubscriptionSwitch = () => {
  const { page, view } = useParams();
  const [{ status }, subscriptionLoading] = useSubscriptionType("saas");

  const companyPageComponents = (page) => {
    switch (page) {
      case "onboarding":
        return <OnboardingChecklist />;
      case "dashboard":
        return <Dashboard />;
      case "settings":
        return <Settings view={view} />;
      case "purchase":
        return <Purchase />;
      case "badges":
        return <Badges />;
      case "reporting":
        return <Reporting />;
      case "accounting":
        return <Accounting />;
      case "events":
        return <Events />;
      case "products":
        return <Products />;
      default:
        return <Dashboard />;
    }
  };

  if (subscriptionLoading) {
    return <LoadingScreen />;
  }

  if (status === "vendor") {
    return <Redirect to="/explorer/dashboard" />;
  }

  if (status === "expired") {
    return <ExpiredAccountFootprintSummary />;
  }

  return (
    <CompanyLayout page={page} view={view}>
      <ErrorBoundary>{companyPageComponents(page)}</ErrorBoundary>
    </CompanyLayout>
  );
};

const PlatformIndex = () => {
  const { accountType } = useParams();
  const growthbook = useGrowthBook();

  const [user, userLoading] = useAuth();

  const [redirect, setRedirect] = useState(false);
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);

  useEffect(() => {
    const authenticateUser = async () => {
      if (!user) {
        // #375: preserve the Smart Upload review deep link (or any other
        // platform URL) through the login round-trip instead of always
        // bouncing to the bare dashboard.
        const redirectTarget = `${window.location.pathname}${window.location.search}`;
        return setRedirect(`/?redirect=${encodeURIComponent(redirectTarget)}`);
      }

      const { claims } = await user.getIdTokenResult({ forceRefresh: true });

      const { company } = claims;
      const accountId = getAccountId();

      if (!company || company !== accountId || accountType !== "company") {
        setRedirect("/");
        return await signOut();
      }

      const { email, displayName, uid } = user;
      analyticsIdentify(user);

      //TODO: disable this in localhost
      if (process.env.NODE_ENV !== "development") {
        Sentry.setUser({ email, id: uid });
      }

      growthbook.setAttributes({
        company: true,
        id: accountId,
        name: displayName,
        email
      });

      if (process.env.REACT_APP_ENVIRONMENT === "production") {
        ReactGA.initialize([{ trackingId: "G-T865T22VKG" }]);
      }

      const companyId = getAccountId();
      if (!companyId) {
        window.sessionStorage.setItem("accountId", company);
      }

      if (!sessionStorage.getItem("individualsBackfilled")) {
        getUserAccountData({ email: user.email, uid: user.uid })
          .then(() => sessionStorage.setItem("individualsBackfilled", "true"))
          .catch(() => null);
      }

      return setIsUserAuthenticated(true);
    };

    if (!userLoading) {
      authenticateUser();
    }
  }, [user, userLoading, growthbook, accountType]);

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  if (!user) {
    return <></>;
  }

  if (!isUserAuthenticated) {
    return <LoadingScreen />;
  }

  return (
    <ErrorBoundary>
      <StripeCustomerContextProvider>
        <SubscriptionSwitch />
      </StripeCustomerContextProvider>
    </ErrorBoundary>
  );
};

export default PlatformIndex;
